import Button from "@/components/Button";
import { useMutation } from "@apollo/client";
import { PhoneNumber } from "@/types";
import { useForm } from "react-hook-form";
import E164PhoneNumberInput from "@/components/E164PhoneNumberInput";
import { UPDATE_PERSON_MUTATION } from "@/features/person/queries";
import { EditProps } from "./types";

type FieldValues = {
  homePhone: string;
  mobilePhone: string;
};

interface PhoneEditModalProps extends EditProps {
  phoneNumbers?: PhoneNumber[];
}

const makePhone = (phoneType: string, phoneNumbers?: PhoneNumber[]) => {
  const { phoneNumber, phoneExtension } = phoneNumbers?.find((p) => p.phoneType === phoneType) || {};
  return phoneNumber ? (phoneExtension ? `${phoneNumber};${phoneExtension}` : phoneNumber) : "";
};

const createDefaultValues = (phoneNumbers?: PhoneNumber[]) => {
  const mobilePhone = makePhone("mobile", phoneNumbers);
  const homePhone = makePhone("home", phoneNumbers);
  return {
    mobilePhone,
    homePhone
  };
};

const defaultErrorMessage = "Failed to update phone numbers";

export default function PhoneEditForm({
  personId,
  phoneNumbers: initialPhoneNumbers,
  onCancel,
  onSuccess,
  onError
}: PhoneEditModalProps) {
  const form = useForm({
    defaultValues: createDefaultValues(initialPhoneNumbers),
    mode: "onChange"
  });

  const [updatePhoneNumbers, { loading }] = useMutation(UPDATE_PERSON_MUTATION);

  async function onSubmit({ homePhone, mobilePhone }: FieldValues) {
    try {
      const phoneNumbers = [
        {
          phoneType: "home",
          phoneNumber: homePhone || null
        },
        {
          phoneType: "mobile",
          phoneNumber: mobilePhone || null
        }
      ];

      const input = {
        id: personId,
        phoneNumbers
      };

      const { data } = await updatePhoneNumbers({
        variables: {
          input
        }
      });

      const {
        updatePerson: { success, person, message }
      } = data;

      if (success) {
        return onSuccess(person);
      }
      return onError(message || defaultErrorMessage);
    } catch (error) {
      return onError((error as Error).message || defaultErrorMessage);
    }
  }

  return (
    <div className="flex flex-col gap-6" data-testid="phone-edit-form">
      <E164PhoneNumberInput
        label="Mobile"
        value={form.watch("mobilePhone")}
        onChange={(value) => form.setValue("mobilePhone", value)}
        error={!!form.formState.errors.mobilePhone}
        errorText={form.formState.errors.mobilePhone?.message}
        onError={(error) => {
          if (error) {
            form.setError("mobilePhone", { message: error });
          } else {
            form.clearErrors("mobilePhone");
            form.trigger("mobilePhone");
          }
        }}
      />
      <E164PhoneNumberInput
        label="Home"
        value={form.watch("homePhone")}
        onChange={(value) => form.setValue("homePhone", value)}
        error={!!form.formState.errors.homePhone}
        errorText={form.formState.errors.homePhone?.message}
        onError={(error) => {
          if (error) {
            form.setError("homePhone", { message: error });
          } else {
            form.clearErrors("homePhone");
            form.trigger("homePhone");
          }
        }}
      />
      <div className="flex flex-wrap justify-end gap-1 items-center">
        <Button variant="text" onClick={onCancel}>
          Cancel
        </Button>
        <Button disabled={loading} onClick={form.handleSubmit(onSubmit)}>
          Save
        </Button>
      </div>
    </div>
  );
}
